import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Phone, Mail, Facebook, Instagram, ChevronUp, Flame } from "lucide-react";
import { company, footerLinks } from "../mock";

const Footer = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const go = (href) => {
    if (href.startsWith("/")) return navigate(href);
    if (pathname !== "/") return navigate("/" + href);
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };
  const Col = ({ title, links }) => (
    <div>
      <h4 className="text-charcoal font-semibold text-[18px]">{title}</h4>
      <span className="block w-[30%] h-[1.1px] bg-fire my-1" />
      <ul className="mt-2">
        {links.map((l) => (
          <li key={l.label}>
            <a
              href={l.href}
              onClick={(e) => { e.preventDefault(); go(l.href); }}
              className="block py-1.5 text-charcoal hover:text-fire transition-colors text-[15px] font-medium"
              data-testid={`footer-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            >
              {l.label}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <footer data-testid="site-footer">
      <div className="px-4 md:px-10 py-6 md:py-10" style={{ backgroundColor: "#E0E0E0" }}>
        <div className="max-w-[1140px] mx-auto grid md:grid-cols-5 gap-8">
          <div className="md:col-span-2 md:p-5">
            <div className="flex items-center gap-2.5 mb-5">
              <span className="w-11 h-11 rounded-sm bg-fire flex items-center justify-center">
                <Flame className="text-white" size={22} />
              </span>
              <span className="leading-none">
                <span className="block font-medium text-charcoal text-2xl">Stove Scotland</span>
                <span className="block uppercase tracking-[0.22em] text-fire text-[9px] mt-1">Supply - Install - Maintain</span>
              </span>
            </div>
            <p className="text-charcoal font-medium text-[16px] leading-[1.3em]">
              Our first priority is to provide you with a professional, honest and reliable service at an affordable
              price. As market experts with a comprehensive knowledge of stoves and fires, we will work with you to
              ensure that your stove will perfectly fit the style and dimensions of your room.
            </p>
            <ul className="mt-5 space-y-2">
              <li>
                <a href={company.phoneHref} className="flex items-center gap-2 text-charcoal font-semibold text-[16px] hover:text-fire transition-colors">
                  <Phone size={14} className="text-fire" /> {company.phone}
                </a>
              </li>
              <li>
                <a href={`mailto:${company.email}`} className="flex items-center gap-2 text-charcoal font-semibold text-[16px] hover:text-fire transition-colors">
                  <Mail size={14} className="text-fire" /> {company.email}
                </a>
              </li>
            </ul>
            <div className="flex gap-3 mt-4">
              <a href={company.facebook} target="_blank" rel="noreferrer" className="text-fire hover:text-charcoal transition-colors" aria-label="Facebook"><Facebook size={20} /></a>
              <a href={company.instagram} target="_blank" rel="noreferrer" className="text-fire hover:text-charcoal transition-colors" aria-label="Instagram"><Instagram size={20} /></a>
            </div>
          </div>

          <div className="md:col-span-3 md:p-5">
            <div className="grid sm:grid-cols-3 gap-6">
              <Col title="Services" links={footerLinks.services} />
              <Col title="Help" links={footerLinks.help} />
              <Col title="Legal" links={footerLinks.legal} />
            </div>
            <div className="mt-8 text-left sm:text-center">
              <h4 className="text-charcoal font-semibold text-[18px]">All Major Credit Cards Accepted</h4>
              <span className="block w-[30%] h-[1.1px] bg-fire my-[7px] sm:mx-auto" />
              <div className="flex gap-2 sm:justify-center mt-3">
                {company.cards.map((c) => (
                  <span key={c} className="bg-white text-charcoal text-xs font-medium px-3 py-1.5 border border-[#DDDADA]">{c}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-fire" style={{ backgroundColor: "#343434" }}>
        <div className="max-w-[1140px] mx-auto px-5 py-3 text-center md:text-left">
          <p className="text-white text-[12px] font-light" data-testid="footer-copyright">
            Stove Scotland {new Date().getFullYear()}. All Rights Reserved
          </p>
        </div>
      </div>

      <button
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        className="fixed bottom-16 md:bottom-6 right-4 z-40 w-10 h-10 bg-fire text-white flex items-center justify-center shadow-md hover:bg-charcoal transition-colors"
        aria-label="Scroll to top"
        data-testid="scroll-top-button"
      >
        <ChevronUp size={22} />
      </button>
    </footer>
  );
};

export default Footer;
